"use client";

import { Bell, LogOut, User } from "lucide-react";
import Link from "next/link";

import { GlobalSearch } from "@/components/search/algolia";
import { ThemeToggle } from "@/components/theme/theme-toggle";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/lib/use-auth";
import { getUserRoleInitials } from "@/lib/utils/user-roles";
import { UserRole } from "@/types/user-roles";

interface Notification {
  id: number;
  type: string;
  message: string;
  unread: boolean;
}

interface PortalHeaderProps {
  showSearch?: boolean;
  hideThemeToggle?: boolean;
  profileLink?: string;
  notifications?: Notification[];
}

export function PortalHeader({
  showSearch = false,
  hideThemeToggle = false,
  profileLink,
  notifications = [],
}: PortalHeaderProps) {
  const { user, signOut, isDemoMode } = useAuth();
  const unreadCount = notifications.filter((n) => n.unread).length;

  const getRoleColor = (role: string) => {
    switch (role?.toLowerCase()) {
      case UserRole.ADMIN:
        return "bg-red-100 text-red-800";
      case UserRole.PROVIDER:
        return "bg-blue-100 text-blue-800";
      case UserRole.NURSE:
        return "bg-green-100 text-green-800";
      case UserRole.STAFF:
        return "bg-gray-100 text-gray-800";
      case UserRole.PATIENT:
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      // Clear any demo cookies
      document.cookie = "demo-auth=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
      document.cookie = "demo-user=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
      document.cookie = "user-role=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    } catch (_error) {}
  };

  return (
    <div className="sticky top-0 z-40 w-full border-b border-gray-200 bg-white shadow-sm">
      <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Left side - Logo */}
        <div className="flex items-center gap-x-4">
          <div className="flex items-center gap-x-6">
            <h1 className="text-2xl font-bold text-blue-600">LyfeAI</h1>
          </div>
        </div>

        {/* Center - Global Search (optional) */}
        {showSearch && (
          <div className="mx-2 flex-1 sm:mx-4 sm:max-w-2xl lg:max-w-3xl">
            <GlobalSearch variant="header" />
          </div>
        )}

        {/* Right side - Theme Toggle, Notifications, and User Profile */}
        <div className="flex items-center gap-x-4">
          {/* Theme Toggle */}
          {!hideThemeToggle && <ThemeToggle variant="healthcare" />}

          {/* Notifications Dropdown - Only show if notifications are provided */}
          {notifications.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                  <Bell className="h-5 w-5 text-gray-600" />
                  {unreadCount > 0 && (
                    <span className="absolute right-0 top-0 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-80 p-0" align="end" forceMount>
                <div className="border-b border-gray-200 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-base font-semibold text-gray-900">Notifications</span>
                    {unreadCount > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {unreadCount} unread
                      </Badge>
                    )}
                  </div>
                </div>
                <ScrollArea className="h-[400px]">
                  {notifications.length === 0 ? (
                    <div className="py-8 text-center text-sm text-gray-500">No notifications</div>
                  ) : (
                    <div className="py-1">
                      {notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className="flex cursor-pointer items-start gap-3 border-b border-gray-100 px-4 py-3 transition-colors last:border-b-0 hover:bg-gray-50"
                        >
                          <Bell
                            className={`mt-0.5 h-4 w-4 flex-shrink-0 ${
                              notification.unread ? "text-blue-600" : "text-gray-400"
                            }`}
                          />
                          <div className="min-w-0 flex-1">
                            <p
                              className={`text-sm ${
                                notification.unread ? "font-semibold text-gray-900" : "font-normal text-gray-600"
                              }`}
                            >
                              {notification.message}
                            </p>
                            <p className="mt-1 text-xs capitalize text-gray-500">{notification.type}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
                {notifications.length > 0 && (
                  <div className="border-t border-gray-200 px-4 py-2">
                    <button
                      type="button"
                      className="w-full text-center text-sm font-medium text-blue-600 transition-colors hover:text-blue-700"
                    >
                      View all notifications
                    </button>
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* User dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                <Avatar className="h-8 w-8">
                  <AvatarImage src="/placeholder.svg?height=32&width=32" alt={user?.name} />
                  <AvatarFallback>
                    {user?.name
                      ?.split(" ")
                      .map((n) => n[0])
                      .join("") || "U"}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">{user?.name}</p>
                  <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
                  {user?.title && <p className="text-xs leading-none text-muted-foreground">{user.title}</p>}
                  {user?.department && <p className="text-xs leading-none text-muted-foreground">{user.department}</p>}
                  <div className="flex flex-wrap gap-1">
                    <Badge className={`w-fit text-xs ${getRoleColor(user?.role || "")}`}>
                      {getUserRoleInitials(user?.role || UserRole.PROVIDER)}
                    </Badge>
                    {isDemoMode && (
                      <Badge variant="secondary" className="w-fit bg-purple-100 text-xs text-purple-800">
                        🚀 Demo
                      </Badge>
                    )}
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {profileLink && (
                <>
                  <DropdownMenuItem asChild>
                    <Link href={profileLink}>
                      <User className="mr-2 h-4 w-4" />
                      <span>Profile</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Sign out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
